import React, { useState, useEffect } from 'react';
import { Page, Card, Layout, Text, Button, BlockStack, TextField, Banner } from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [shop, setShop] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Verificar se está embedado no Shopify - se sim, não mostrar esta página
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shopParam = urlParams.get('shop');
    const host = urlParams.get('host');
    
    // Se estiver embedado, não mostrar login (OAuth é automático)
    if (shopParam && host) {
      // Não fazer nada, o AuthContext vai iniciar OAuth automaticamente
      return;
    }
    
    // Se tiver shop na URL mas sem host, preencher campo
    if (shopParam) {
      setShop(shopParam);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!shop) {
      setError('Por favor, digite o domínio da loja');
      return;
    }

    // Normalizar e validar formato do shop
    let shopDomain = shop.trim().toLowerCase();
    
    // Remover espaços e caracteres extras
    shopDomain = shopDomain.replace(/\s+/g, '');
    
    // Remover http://, https://, www. se houver
    shopDomain = shopDomain.replace(/^https?:\/\//, '');
    shopDomain = shopDomain.replace(/^www\./, '');
    shopDomain = shopDomain.replace(/\/.*$/, ''); // Remover tudo depois da barra
    
    // Se não tiver .myshopify.com, adicionar
    if (!shopDomain.includes('.myshopify.com')) {
      shopDomain = `${shopDomain}.myshopify.com`;
    }
    
    // Validar formato final (apenas letras, números e hífen)
    if (!shopDomain.match(/^[a-zA-Z0-9-]+\.myshopify\.com$/)) {
      setError('Domínio inválido. Digite apenas o nome da loja (ex: ishop-management) ou o domínio completo');
      return;
    }

    login(shopDomain);
  };

  return (
    <Page narrowWidth>
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="500">
              <BlockStack gap="200">
                <Text variant="heading2xl" as="h1">
                  {t('login.title', { defaultValue: 'Welcome to Reviews App' })}
                </Text>
                <Text as="p" tone="subdued">
                  {t('login.description', { 
                    defaultValue: 'Connect your Shopify store to start managing product reviews' 
                  })}
                </Text>
              </BlockStack>

              {error && (
                <Banner tone="critical" onDismiss={() => setError('')}>
                  {error}
                </Banner>
              )}

              <form onSubmit={handleSubmit}>
                <BlockStack gap="400">
                  <TextField
                    label={t('login.shopLabel', { defaultValue: 'Shop Domain' })}
                    value={shop}
                    onChange={setShop}
                    placeholder="ishop-management ou ishop-management.myshopify.com"
                    autoComplete="off"
                    helpText={t('login.shopHelp', { 
                      defaultValue: 'Digite apenas o nome da loja (ex: ishop-management) ou o domínio completo' 
                    })}
                  />

                  <Button submit variant="primary" fullWidth>
                    {t('login.connect', { defaultValue: 'Connect Store' })}
                  </Button>
                </BlockStack>
              </form>

              <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                {t('login.help', { 
                  defaultValue: 'You will be redirected to Shopify to authorize this app' 
                })}
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default Login;

